import { PartFinderBatch } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useClearPartFinderBatchName } from '@/hooks/api/part-finder-batches/useClearPartFinderBatchName';
import { useUpdatePartFinderBatchName } from '@/hooks/api/part-finder-batches/useUpdatePartFinderBatchName';
import { useToast } from '@/hooks/useToast';
import { getApiError } from '@/types/apiError';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useState } from 'react';

const MAX_NAME_LENGTH = 512;

export interface RenameBatchDialogProperties {
  batch: PartFinderBatch;
  open: boolean;
  onClose: () => void;
}

export const RenameBatchDialog = ({ batch, open, onClose }: RenameBatchDialogProperties) => {
  const [value, setValue] = useState(batch.name ?? '');
  const updateMutation = useUpdatePartFinderBatchName(batch.id);
  const clearMutation = useClearPartFinderBatchName(batch.id);
  const { showToast } = useToast();

  const isPending = updateMutation.isPending || clearMutation.isPending;
  const activeError = updateMutation.error ?? clearMutation.error;
  const trimmed = value.trim();
  const canSave = trimmed !== (batch.name ?? '').trim() && !isPending;
  const apiError = activeError ? getApiError(activeError, 'Failed to rename batch') : undefined;

  const handleClose = () => {
    if (isPending) return;
    updateMutation.reset();
    clearMutation.reset();
    onClose();
  };

  const handleSave = () => {
    if (!canSave) return;
    const onSuccess = () => {
      showToast({ message: trimmed === '' ? 'Batch name cleared' : 'Batch renamed', type: 'success' });
      onClose();
    };
    if (trimmed === '') {
      clearMutation.mutate(undefined, { onSuccess });
    } else {
      updateMutation.mutate({ name: trimmed }, { onSuccess });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Rename batch</DialogTitle>
      <DialogContent>
        <ApiErrorAlert error={apiError} />
        <TextField
          value={value}
          onChange={event => setValue(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') handleSave();
          }}
          placeholder='e.g. Q2 hydraulics sourcing'
          fullWidth
          autoFocus
          disabled={isPending}
          sx={{ marginTop: '8px' }}
          slotProps={{ htmlInput: { maxLength: MAX_NAME_LENGTH, 'aria-label': 'Batch name' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant='contained'
          color='primary'
          startIcon={isPending ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
